import type { MemoryEntry } from '@proj-airi/server-sdk-shared'

import type { DocumentFields, LocalDocumentChanges, SyncState } from '../document-sync'

import { memoryEntrySchema, memoryKey } from '@proj-airi/server-sdk-shared'
import { isEqual } from 'es-toolkit'
import { nanoid } from 'nanoid'

import * as v from 'valibot'

function readEntries(fields: DocumentFields): MemoryEntry[] | undefined {
  const result = v.safeParse(v.record(v.string(), memoryEntrySchema), fields)
  if (!result.success)
    return undefined
  if (Object.entries(result.output).some(([key, entry]) => memoryKey(entry) !== key))
    return undefined
  return Object.values(result.output)
}

/** Projects complete facts into independently versioned fields. Empty memory removes the collection. */
export function splitMemories(entries: readonly MemoryEntry[]): Record<string, DocumentFields> {
  return entries.length > 0
    ? { entries: Object.fromEntries(entries.map(entry => [memoryKey(entry), entry])) }
    : {}
}

/**
 * Applies remote facts without losing a conflicting local fact. A conflict receives a new name in its original scope.
 * Invalid remote content keeps its previous sync state, so a later run cannot interpret rejection as deletion.
 */
export function applyMemoryChanges(current: readonly MemoryEntry[], changes: LocalDocumentChanges, state: SyncState) {
  let entries = [...current]
  const rejected: string[] = []
  for (const [id, fields] of Object.entries(changes.upserts)) {
    const parsed = id === 'entries' ? readEntries(fields) : undefined
    if (!parsed) {
      rejected.push(id)
      continue
    }
    entries = parsed
  }
  if (changes.removals.includes('entries'))
    entries = []

  for (const copy of changes.conflictCopies) {
    if (copy.documentId !== 'entries' || rejected.includes(copy.documentId))
      continue
    const local = readEntries(copy.fields)
    if (!local) {
      rejected.push(copy.documentId)
      continue
    }
    for (const entry of local) {
      // A conflict copy contains the whole collection. An unchanged fact that another device deleted must stay deleted.
      const baseline = state.documents.entries?.fields[memoryKey(entry)]?.value
      if (isEqual(entry, baseline))
        continue
      const canonical = entries.find(other => memoryKey(other) === memoryKey(entry))
      if (isEqual(entry, canonical))
        continue
      entries.push({
        ...entry,
        name: `${entry.name.slice(0, 48).replace(/-+$/g, '')}-conflict-${nanoid(6).toLowerCase().replace(/[^a-z0-9]/g, 'x')}`,
        updatedAt: Date.now(),
      })
    }
  }
  return { entries, rejected }
}
